/**
 * Height-bounded, scrollable column of evaluated laboratory markers.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { LabResultItem } from '../../../types';
import { LabResultCard } from './LabResultCard';

/**
 * Properties for the marker column beside the trend graph.
 */
interface LabMarkerColumnProps {
  evaluatedItems: LabResultItem[];
  notEvaluatedItems: LabResultItem[];
  selectedKey: string | null;
  onSelect: (item: LabResultItem) => void;
}

/**
 * Render the evaluated markers as an internally scrollable pane with a
 * collapsible "Not evaluated" disclosure pinned directly beneath them.
 *
 * The inner column is absolutely positioned inside a relative wrapper, so it
 * contributes no intrinsic height: the grid row is sized by the trend graph and
 * the wrapper stretches to match it. The evaluated list scrolls internally,
 * while the not-evaluated disclosure sits at the bottom of the column (below the
 * cards) so it is always discoverable next to the graph. The active card is
 * scrolled into view whenever the selection changes.
 *
 * @param props - Marker groups, current selection, and the selection handler.
 * @returns The bounded marker column with a not-evaluated footer.
 */
export function LabMarkerColumn({
  evaluatedItems,
  notEvaluatedItems,
  selectedKey,
  onSelect,
}: LabMarkerColumnProps) {
  const evaluatedScrollRef = useRef<HTMLDivElement>(null);
  const isFirstRun = useRef(true);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  /**
   * Recompute whether the evaluated list overflows and is not yet at the bottom.
   *
   * @returns void
   */
  const updateScrollCue = (): void => {
    const element = evaluatedScrollRef.current;
    if (!element) return;
    const remaining = element.scrollHeight - element.clientHeight - element.scrollTop;
    setHasMoreBelow(remaining > 4);
  };

  // Re-measure when the marker set changes; macOS hides overlay scrollbars, so
  // the fade cue and header hint are what tell nurses more markers exist below.
  useLayoutEffect(updateScrollCue, [evaluatedItems.length]);

  // Re-measure on viewport resize, which changes the available pane height.
  useEffect(() => {
    window.addEventListener('resize', updateScrollCue);
    return () => window.removeEventListener('resize', updateScrollCue);
  }, []);

  // Keep the active card visible on selection change by scrolling only the
  // marker pane (never the document, and never on the initial seeded selection).
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const container = evaluatedScrollRef.current;
    const selectedCard = container?.querySelector<HTMLElement>(
      '.lab-result-card-selected',
    );
    if (!container || !selectedCard) return;
    // Use viewport rects so the math is independent of offsetParent: the card's
    // offsetParent is the absolutely-positioned column, not this scroll pane, so
    // offsetTop would be skewed by the pane header height.
    const cardBox = selectedCard.getBoundingClientRect();
    const paneBox = container.getBoundingClientRect();
    const overflowTop = cardBox.top - paneBox.top;
    const overflowBottom = cardBox.bottom - paneBox.bottom;
    if (overflowTop < 0) {
      container.scrollTop += overflowTop;
    } else if (overflowBottom > 0) {
      container.scrollTop += overflowBottom;
    }
  }, [selectedKey]);

  return (
    <div className="lab-marker-column-wrap">
      <div className="lab-marker-column">
        <div className="lab-marker-pane lab-marker-pane--evaluated">
          <div className="lab-marker-pane-header">
            <span>Evaluated ({evaluatedItems.length})</span>
            {hasMoreBelow && (
              <span className="lab-marker-scroll-hint">Scroll for more</span>
            )}
          </div>
          <div
            className="lab-marker-pane-scroll"
            ref={evaluatedScrollRef}
            onScroll={updateScrollCue}
          >
            {evaluatedItems.map((item) => (
              <LabResultCard
                key={item.key}
                item={item}
                selected={selectedKey === item.key}
                onSelect={onSelect}
              />
            ))}
          </div>
          <div
            className={`lab-marker-scroll-cue${hasMoreBelow ? ' lab-marker-scroll-cue--visible' : ''}`}
            aria-hidden="true"
          />
        </div>

        {notEvaluatedItems.length > 0 && (
          <details className="lab-not-evaluated-group">
            <summary className="lab-not-evaluated-summary">
              Not evaluated ({notEvaluatedItems.length})
            </summary>
            <div className="lab-not-evaluated-cards">
              {notEvaluatedItems.map((item) => (
                <LabResultCard key={item.key} item={item} selected={false} />
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

export default LabMarkerColumn;
